import { CheckCircle2, RotateCcw, XCircle, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useBlocker } from 'react-router-dom';

import { DateNeighborhood } from './DateNeighborhood';
import { DECISION_TITLES, DecisionForm } from './DecisionForm';
import { useDateNeighborhood } from '../../hooks/useDateNeighborhood';
import { useDecideProject } from '../../hooks/useDecideProject';
import { useSpaceConflicts } from '../../hooks/useSpaceConflicts';
import { Button } from '@/shared/components/ui/button';
import { Sheet } from '@/shared/components/ui/sheet';
import { messages } from '@/shared/constants/messages';
import { leadTimeWarning } from '@/shared/constants/projectSizes';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { extractRpcCode } from '@/shared/lib/errors';
import { cn } from '@/shared/lib/utils';
import type { ProjectDecision, ProjectWithContext } from '@/shared/types/domain';

type Mode = 'idle' | ProjectDecision;

/**
 * Cada verbo veste **o ícone do estado que ele produz** (decisão 41), saído do
 * mapa que `StatusBadge` já mantém — não é iconografia nova.
 *
 * `CheckCircle2` é o ícone reservado a `approved` pelo docblock do
 * `StatusBadge`, e usá-lo aqui **reforça** a reserva em vez de diluí-la: este é
 * o botão que produz aquele crachá.
 *
 * **"Aprovar" veste o azul da marca (`default`), não o verde de `approved`.**
 * Ele é a ação da tela — o desfecho que a análise existe para produzir —, e é
 * assim que o painel diz qual dos três é o caminho normal. O verde continua
 * dizendo `approved` no crachá, onde ele descreve um FATO; aqui a cor precisa
 * dizer "clique aqui", que é trabalho do azul em todo o resto do painel.
 *
 * Só "Aprovar" é preenchido. Os dois destrutivos são `outline` e revelam a cor
 * no hover **e no foco por teclado** — quem navega por Tab precisa da mesma
 * confirmação de que o mouse dá.
 */
const VERB_STYLES: Record<
  ProjectDecision,
  { icon: LucideIcon; variant: 'default' | 'outline'; className?: string }
> = {
  approve: { icon: CheckCircle2, variant: 'default' },
  request_changes: {
    icon: RotateCcw,
    variant: 'outline',
    className:
      'hover:border-amber-300 hover:bg-amber-50 hover:text-amber-900 ' +
      'focus-visible:border-amber-300 focus-visible:bg-amber-50 focus-visible:text-amber-900',
  },
  reject: {
    icon: XCircle,
    variant: 'outline',
    className:
      'hover:border-rose-300 hover:bg-rose-50 hover:text-rose-900 ' +
      'focus-visible:border-rose-300 focus-visible:bg-rose-50 focus-visible:text-rose-900',
  },
};

const VERB_ORDER: readonly ProjectDecision[] = ['approve', 'request_changes', 'reject'];

interface DecisionPanelProps {
  project: ProjectWithContext;
  /** A decisão desta pessoa foi registrada (§2.4) — o painel dá lugar ao retorno. */
  onDecided: () => void;
  /** Outra pessoa decidiu primeiro (§5.4): a tela se corrige, ninguém recarrega nada. */
  onLostRace: () => void;
}

/**
 * A coluna do revisor, encolhida para **contexto → ação** (decisão 36), com os
 * três verbos **direto na tela** (decisão 107).
 *
 * ⛔ **Nunca reintroduza um passo antes dos verbos** — nem "Iniciar análise",
 * nem "Analisar este projeto", nem RPC `start_review`. As decisões 27 e 39
 * puseram os verbos atrás de um botão que era `useState` puro: sem RPC, sem
 * log, sem trava, sem nada que outra pessoa visse. A decisão 107 removeu o
 * botão porque ele cobrava um clique em toda análise real e não protegia estado
 * nenhum — e porque o nome tinha o defeito de parecer transição.
 *
 * A reivindicação de análise saiu do produto inteiro na decisão 25: o dado
 * interessante nunca foi *quem está olhando agora*, e sim quem decidiu o quê,
 * que já é gravado. Reivindicação efêmera não muda poder nem compromisso e,
 * pelo padrão 3 da casa, não devia estar no log. A [ADR-013] apagou
 * `start_review` e `release_review` do banco — não há o que chamar.
 *
 * **O que protege contra o clique acidental não é um portão, é o segundo
 * passo:** todo verbo abre `DecisionForm`, com observação e "Confirmar". Isso
 * importa porque o revisor não chega mais só pela `/analise` — desde a
 * [ADR-015] ele cai aqui passeando pelo calendário da filial.
 *
 * **Os alertas saíram desta coluna** (decisão 36) e foram para o lado do fato
 * que eles qualificam, no documento: conflito junto de "Local", porte junto de
 * "Data e Hora". Isto emenda a decisão 19 no que ela tinha de posição, não no
 * que ela tinha de princípio — alerta antes de ação continua valendo, e agora
 * ele vem antes de a pessoa sequer chegar aos verbos. O que fica aqui é o
 * contexto que não pertence a fato nenhum (a vizinhança da data) e a ação.
 *
 * **Uma ordem só nos dois tamanhos.** Ordem que muda por breakpoint é onde a
 * regressão nasce.
 *
 * **Desktop (`lg+`): o formulário substitui o bloco NO LUGAR.** Nada de modal —
 * o revisor precisa reler o documento à esquerda enquanto escreve. **Em 375px
 * os verbos abrem uma folha de tela cheia** (decisão 10 / §6.4): a coluna já
 * virou bloco no topo do documento, e substituir o bloco inteiro empurraria o
 * documento para baixo do que a pessoa já tinha lido.
 */
export function DecisionPanel({ project, onDecided, onLostRace }: DecisionPanelProps) {
  const [mode, setMode] = useState<Mode>('idle');
  const [comment, setComment] = useState('');
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const conflictsQuery = useSpaceConflicts(project.id);
  const neighborhoodQuery = useDateNeighborhood(
    project.id,
    project.branch_id,
    project.starts_at,
    project.ends_at,
  );
  const decide = useDecideProject();

  const conflicts = conflictsQuery.data ?? [];
  const leadTime = leadTimeWarning(project.expected_audience, project.starts_at);

  /**
   * Sair no meio de uma análise com texto escrito pede confirmação (decisão 40).
   *
   * **O gatilho é o texto, não o verbo escolhido.** Abrir o formulário e não
   * escrever nada não perde nada: obstáculo sem objeto atrás treina o dedo a
   * confirmar sem ler, inclusive no dia em que houver objeto.
   *
   * `useBlocker` cobre a navegação DENTRO do SPA — voltar, sidebar, breadcrumb,
   * clique em ministério. **Fechar a aba ou recarregar fica de fora, e é
   * deliberado:** só `beforeunload` faria isso, e o texto dele é do navegador,
   * em inglês, sem controle nosso — o oposto do RNF-U03.
   */
  const hasComment = comment.trim().length > 0;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasComment && !decide.isPending && currentLocation.pathname !== nextLocation.pathname,
  );

  function closeMode() {
    setMode('idle');
    // O texto SOME ao sair da tela do projeto (o componente desmonta), mas
    // sobrevive a "Voltar" — por isso não é limpo aqui, só na confirmação.
  }

  function handleConfirm() {
    if (mode === 'idle') return;
    decide.mutate(
      {
        projectId: project.id,
        ministryId: project.ministry_id,
        decision: mode,
        comment: comment.trim() || null,
      },
      {
        onSuccess: () => {
          setMode('idle');
          setComment('');
          onDecided();
        },
        onError: (error) => {
          // Perder a corrida não é falha de formulário: não há nada para o
          // usuário corrigir e nada para reenviar. A tela inteira muda de
          // estado, e quem cuida disso é a página (§5.4). O texto digitado
          // some junto com o painel — escrever de novo não teria para onde ir.
          if (extractRpcCode(error) === 'already_decided') onLostRace();
        },
      },
    );
  }

  const form = mode !== 'idle' && (
    <DecisionForm
      decision={mode}
      comment={comment}
      onChangeComment={setComment}
      conflicts={conflicts}
      leadTime={leadTime}
      isSubmitting={decide.isPending}
      error={decide.error}
      onBack={closeMode}
      onConfirm={handleConfirm}
    />
  );

  // No desktop, o formulário substitui ESTE bloco inteiro (§6.4: "a coluna
  // vira o formulário no lugar") — não some por trás dele. No celular ele
  // continua embaixo, porque a folha flutua por cima como camada separada.
  const showContext = !isDesktop || mode === 'idle';

  return (
    <div className="space-y-6">
      <h2 className="text-sm font-semibold text-muted-foreground">{messages.review.decisionTitle}</h2>

      {showContext ? (
        /* O único contexto que sobrou aqui: ele não pertence a fato nenhum do
           documento — é sobre a AGENDA da igreja, não sobre este projeto. Os
           dois alertas que moravam abaixo foram para o lado dos fatos que
           qualificam (decisão 36). */
        <DateNeighborhood
          currentProjectId={project.id}
          currentStartsAt={project.starts_at}
          currentEndsAt={project.ends_at}
          items={neighborhoodQuery.data ?? []}
          isLoading={neighborhoodQuery.isPending}
          collapsedByDefault={!isDesktop}
        />
      ) : null}

      {/* Desktop: o formulário substitui este bloco. Celular: os botões
          continuam de pé, e o formulário abre por cima numa folha. */}
      {isDesktop && mode !== 'idle' ? (
        <div className="space-y-4">
          <h2 className="text-base font-semibold">{DECISION_TITLES[mode]}</h2>
          {form}
        </div>
      ) : (
        <div className="space-y-2 border-t border-border pt-4">
          {VERB_ORDER.map((decision) => {
            const { icon: Icon, variant, className } = VERB_STYLES[decision];
            return (
              <Button
                key={decision}
                type="button"
                variant={variant}
                className={cn('w-full', className)}
                onClick={() => setMode(decision)}
              >
                <Icon aria-hidden="true" className="size-4" />
                {messages.review.actions[toActionKey(decision)]}
              </Button>
            );
          })}
        </div>
      )}

      {!isDesktop ? (
        <Sheet
          open={mode !== 'idle'}
          onClose={closeMode}
          placement="bottom"
          dismissible={!decide.isPending}
          title={mode !== 'idle' ? messages.review.actions[toActionKey(mode)] : ''}
        >
          {form}
        </Sheet>
      ) : null}

      {/* `center`, o mesmo padrão da confirmação de cancelamento: pergunta de
          uma linha, sem arrasto. Componente novo nenhum. */}
      <Sheet
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        placement="center"
        title={messages.review.exitGuardTitle}
        description={messages.review.exitGuardDescription}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => blocker.reset?.()}>
              {messages.review.exitGuardStay}
            </Button>
            <Button type="button" variant="destructive" onClick={() => blocker.proceed?.()}>
              {messages.review.exitGuardLeave}
            </Button>
          </div>
        }
      >
        {null}
      </Sheet>
    </div>
  );
}

function toActionKey(decision: ProjectDecision): 'approve' | 'requestChanges' | 'reject' {
  if (decision === 'request_changes') return 'requestChanges';
  return decision;
}

/* `LeadTimeAlert` morava aqui e mudou para `LeadTimeAlert.tsx` (decisão 36):
   ele passou a ter dois consumidores — o documento, ao lado de "Data e Hora", e
   `DecisionForm` no ramo `approve`. */
